/**
 * Candidate · "Complete your application" — the page behind the secure link in the email.
 *
 *   GET  /candidate-profile/{token}          what am I being asked for
 *   POST /candidate-profile/{token}/extract  read my CV and prefill what it can
 *   POST /candidate-profile/{token}/submit   my details + my CV
 *
 * NO LOGIN. Holding the link is the authorisation — a candidate has no account, and demanding
 * one before they can send a CV loses most of them. The token is a capability, exactly as the
 * interview link is, and it is scoped to this one action.
 *
 * LAYOUT. The form on the left, and on the right a live card of how the candidate will be
 * presented to the hiring team. A field reaches the card when the candidate finishes it (leaves
 * the field) and only if it checks out — an invalid email is flagged under its field and never
 * shown on the card. Every value is self-reported and is never scored: it is context for a person.
 *
 * CTC and visa appear only when the employer switched those fields on for the role (0090) — a
 * candidate is never asked for pay the team didn't want to collect.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, CSSProperties, DragEvent, FormEvent, ReactNode, RefObject } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import "../../styles/complete-profile.css";

interface ProfileRequest {
  jobTitle: string;
  companyName: string;
  needsResume: boolean;
  needsProfile: boolean;
  note: string | null;
  expiresAt: string;
  completed: boolean;
  candidateName: string | null;
  email: string | null;
  linkedinUrl: string | null;
  /** Optional detail fields this job asks for (0090). Absent on an older API → none. */
  candidateFields?: string[];
}

interface ExtractedFacts {
  candidateName?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
  location?: string | null;
  experienceYears?: number | null;
  noticePeriodDays?: number | null;
}

interface SubmitResult {
  ok: boolean;
  resumeReceived: boolean;
  resumeReadable: boolean;
  jobTitle: string;
  companyName: string;
}

/** 10 MB — the ceiling the API enforces. Checked here too so a big file fails instantly. */
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".pdf,.doc,.docx,.rtf,.odt,.txt,application/pdf";
const MAX_SKILLS = 12;
const YEAR_NOW = new Date().getFullYear();

/** Read a File to base64 without pulling in a dependency, chunked so a large CV can't blow the stack. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

// ── fields ────────────────────────────────────────────────────────────────────

type TextKey =
  | "name" | "email" | "phone" | "linkedin" | "location" | "experience" | "grad" | "about"
  | "notice" | "noticeDays" | "lwd" | "currentCtc" | "expectedCtc" | "visa";

const NOTICE_OPTIONS: { value: string; label: string }[] = [
  { value: "0", label: "Immediate" },
  { value: "15", label: "15 days" },
  { value: "30", label: "30 days" },
  { value: "60", label: "60 days" },
  { value: "90", label: "90 days" },
  { value: "more", label: "More than 90 days" },
];

const LABEL: Record<TextKey | "skills" | "resume", string> = {
  name: "Full name", email: "Email", phone: "Phone", linkedin: "LinkedIn", location: "City",
  experience: "Years of experience", grad: "Graduation year", about: "About you", notice: "Notice period",
  noticeDays: "Notice period (days)", lwd: "Last working date", currentCtc: "Current CTC",
  expectedCtc: "Expected CTC", visa: "Visa sponsorship", skills: "Skills", resume: "Resume",
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);
const addDays = (iso: string, n: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** One message per problem, written as what to do — never just "invalid". Empty string = fine. */
function validate(k: TextKey, raw: string): string {
  const v = raw.trim();
  switch (k) {
    case "name":
      if (!v) return "";
      if (v.length < 2) return "Enter your full name.";
      return /^[\p{L}][\p{L}\s.'-]*$/u.test(v) ? "" : "Use letters, spaces, apostrophes or hyphens only.";
    case "email":
      if (!v) return "";
      if (!v.includes("@")) return "An email needs an @ — for example name@example.com.";
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? "" : "Check the part after the @ — for example name@example.com.";
    case "phone": {
      if (!v) return "";
      if (/[^\d\s+()-]/.test(v)) return "Use digits only (spaces, + and - are fine).";
      const n = v.replace(/\D/g, "").length;
      return n >= 7 && n <= 15 ? "" : "Phone numbers are 7 to 15 digits long.";
    }
    case "linkedin":
      if (!v) return "";
      return /(^|\/\/|\.)linkedin\.com\/\S+/i.test(v) ? "" : "Paste your profile link — for example linkedin.com/in/your-name.";
    case "location":
      return v && v.length < 2 ? "Enter your city." : "";
    case "experience": {
      if (!v) return "";
      const n = Number(v);
      return Number.isInteger(n) && n >= 0 && n <= 60 ? "" : "Enter whole years between 0 and 60.";
    }
    case "grad": {
      if (!v) return "";
      if (!/^\d{4}$/.test(v)) return "Enter a 4-digit year, for example 2024.";
      const y = Number(v);
      return y >= 1960 && y <= YEAR_NOW + 8 ? "" : `Enter a year between 1960 and ${YEAR_NOW + 8}.`;
    }
    case "noticeDays": {
      const n = Number(v);
      if (!v) return "Enter your notice period in days.";
      return Number.isInteger(n) && n > 90 && n <= 365 ? "" : "Enter a number of days from 91 to 365.";
    }
    case "lwd": {
      if (!v) return "";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || Number.isNaN(Date.parse(v))) return "Enter a real date.";
      if (v < addDays(todayIso(), -365 * 5)) return "That date is more than 5 years ago.";
      if (v > addDays(todayIso(), 90)) return "An immediate joiner's last day should be within the next 90 days.";
      return "";
    }
    case "currentCtc":
    case "expectedCtc":
      if (!v) return "";
      if (v.length > 60) return "Keep this under 60 characters.";
      return /\d/.test(v) ? "" : "Include an amount — for example ₹12 LPA.";
    default:
      return "";
  }
}

// ── motion helpers ────────────────────────────────────────────────────────────

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The value flies from the field to its place on the card (shared-element style). */
function flyTo(from: Element | null, to: Element | null, text: string): Promise<void> {
  return new Promise((resolve) => {
    if (!from || !to || !text || prefersReducedMotion()) return resolve();
    const a = from.getBoundingClientRect();
    const b = to.getBoundingClientRect();
    const onScreen = (r: DOMRect): boolean => r.bottom > 0 && r.top < window.innerHeight;
    if (!onScreen(a) || !onScreen(b)) return resolve();
    const g = document.createElement("div");
    g.className = "cp-ghost";
    g.textContent = text.length > 34 ? `${text.slice(0, 33)}…` : text;
    g.setAttribute("aria-hidden", "true");
    document.body.append(g);
    const w = g.offsetWidth;
    const h = g.offsetHeight;
    const x0 = a.left + 30;
    const y0 = a.top + a.height / 2 - h / 2;
    const x1 = b.left + b.width / 2 - w / 2;
    const y1 = b.top + b.height / 2 - h / 2;
    const arcX = (x0 + x1) / 2;
    const arcY = Math.min(y0, y1) - 40;
    const anim = g.animate(
      [
        { transform: `translate(${x0}px, ${y0}px) scale(.92)`, opacity: 0 },
        { transform: `translate(${x0 + 6}px, ${y0 - 6}px) scale(1)`, opacity: 1, offset: 0.12 },
        { transform: `translate(${arcX}px, ${arcY}px) scale(1.04)`, opacity: 1, offset: 0.55 },
        { transform: `translate(${x1}px, ${y1}px) scale(.9)`, opacity: 0.15 },
      ],
      { duration: 620, easing: "cubic-bezier(.2,.8,.2,1)" },
    );
    const done = (): void => {
      g.remove();
      resolve();
    };
    anim.onfinish = done;
    anim.oncancel = done;
  });
}

// ── card model ────────────────────────────────────────────────────────────────

/** What the CARD shows: only values the candidate finished and that passed validation. */
type CardState = Partial<Record<TextKey, string>> & { skills: string[]; resume: string };

const initials = (n: string): string =>
  n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

function hueOf(n: string): number {
  let h = 0;
  for (const ch of n) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return (h % 60) + 150; // stay in the brand's teal–blue family
}

const formatPhone = (v: string): string => {
  const d = v.replace(/[^\d+]/g, "");
  return /^\d{10}$/.test(d) ? `${d.slice(0, 5)} ${d.slice(5)}` : v.replace(/\s+/g, " ").trim();
};
const noticeText = (card: CardState): string => {
  if (card.notice === undefined) return "";
  if (card.notice === "more") return card.noticeDays ? `${card.noticeDays} days` : "";
  return NOTICE_OPTIONS.find((o) => o.value === card.notice)?.label ?? "";
};
const dateText = (iso?: string): string =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";

// ── the page ──────────────────────────────────────────────────────────────────

export function CompleteProfile(): ReactNode {
  const { token = "" } = useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["candidate-profile", token],
    queryFn: () => api.get<{ request: ProfileRequest }>(`/candidate-profile/${token}`),
    enabled: token.length > 0,
    retry: false,
  });
  const reqInfo = data?.request;

  if (isLoading) {
    return (
      <Frame>
        <p className="cp-mute">Loading your application…</p>
      </Frame>
    );
  }
  if (isError || !reqInfo) {
    const msg =
      error instanceof ApiError && typeof error.body === "object" && error.body !== null
        ? ((error.body as { message?: string }).message ?? "This link isn't valid any more.")
        : "This link isn't valid any more.";
    return (
      <Frame>
        <div className="cp-panel cp-center">
          <div className="cp-banner crit" role="alert">
            <IconAlert />
            <div>
              <b>{msg}</b>
              <div>If you still want to apply, reply to the email you received and ask for a new link.</div>
            </div>
          </div>
        </div>
      </Frame>
    );
  }
  return <ProfileForm token={token} reqInfo={reqInfo} />;
}

function ProfileForm({ token, reqInfo }: { token: string; reqInfo: ProfileRequest }): ReactNode {
  const asks = useCallback((f: string): boolean => reqInfo.candidateFields?.includes(f) ?? false, [reqInfo]);
  const fileRef = useRef<HTMLInputElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLElement>(null);

  // Drafts (what's typed) vs the card (what's finished and valid). Prefilled from what the
  // recruiter already had, so the candidate corrects rather than retypes.
  const prefill = {
    name: reqInfo.candidateName ?? "",
    email: reqInfo.email ?? "",
    linkedin: reqInfo.linkedinUrl ?? "",
  };
  const [draft, setDraft] = useState<Partial<Record<TextKey, string>>>(prefill);
  const [errors, setErrors] = useState<Partial<Record<TextKey | "skills" | "resume", string>>>({});
  const [card, setCard] = useState<CardState>(() => {
    const c: CardState = { skills: [], resume: "" };
    for (const [k, v] of Object.entries(prefill) as [TextKey, string][]) if (v && !validate(k, v)) c[k] = v.trim();
    return c;
  });
  const [skills, setSkills] = useState<string[]>([]);
  const [skillDraft, setSkillDraft] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractNote, setExtractNote] = useState<string | null>(null);
  const [flashing, setFlashing] = useState<Record<string, number>>({});
  const [shaking, setShaking] = useState<Record<string, number>>({});
  const [summary, setSummary] = useState<[string, string][]>([]);
  const [announce, setAnnounce] = useState("");
  const [result, setResult] = useState<SubmitResult | null>(null);

  const set = (k: TextKey, v: string): void => {
    setDraft((d) => ({ ...d, [k]: v }));
    // Reward early: once a flagged field is fixed, clear its error while typing.
    if (errors[k] && !validate(k, v)) setErrors((e) => ({ ...e, [k]: "" }));
  };
  const flash = (slot: string): void => {
    if (prefersReducedMotion()) return;
    setFlashing((f) => ({ ...f, [slot]: (f[slot] ?? 0) + 1 }));
  };
  const slotEl = (slot: string): Element | null => cardRef.current?.querySelector(`[data-slot="${slot}"]`) ?? null;

  /** A field was finished: check it, then put it on the card (or take it off). */
  const finish = async (k: TextKey, fromEl: Element | null, fresh?: string): Promise<void> => {
    const raw = fresh ?? draft[k] ?? "";
    const v = raw.trim();
    let err = validate(k, raw);
    if (k === "noticeDays" && draft.notice !== "more") err = "";
    setErrors((e) => ({ ...e, [k]: err }));
    if (err) {
      if (!prefersReducedMotion()) setShaking((s) => ({ ...s, [k]: (s[k] ?? 0) + 1 }));
      if (card[k] !== undefined) setCard((c) => ({ ...c, [k]: undefined })); // never keep a value that no longer checks out
      return;
    }
    const next = v || undefined;
    if (card[k] === next) return;
    const slot = slotFor(k);
    if (next) {
      const shown =
        k === "phone" ? formatPhone(v)
        : k === "notice" ? NOTICE_OPTIONS.find((o) => o.value === v)?.label ?? v
        : k === "lwd" ? dateText(v)
        : v;
      await flyTo(fromEl, slotEl(slot), shown);
      setCard((c) => ({ ...c, [k]: next }));
      flash(slot);
      setAnnounce(`${LABEL[k]} added to your card.`);
    } else {
      setCard((c) => ({ ...c, [k]: undefined }));
    }
  };

  // Changing the notice period away from Immediate / "more" drops the dependent answer.
  useEffect(() => {
    if (draft.notice !== "0" && card.lwd) setCard((c) => ({ ...c, lwd: undefined }));
    if (draft.notice !== "more" && card.noticeDays) setCard((c) => ({ ...c, noticeDays: undefined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.notice]);

  // ── skills ──────────────────────────────────────────────────────────────
  const addSkill = async (raw: string, fromEl: Element | null): Promise<void> => {
    const t = raw.replace(/\s+/g, " ").trim().replace(/^,+|,+$/g, "");
    if (!t) return;
    let err = "";
    if (skills.some((s) => s.toLowerCase() === t.toLowerCase())) err = `“${t}” is already on your list.`;
    else if (skills.length >= MAX_SKILLS) err = `You can add up to ${MAX_SKILLS} skills — remove one to add another.`;
    else if (t.length > 40) err = "Keep each skill under 40 characters.";
    setErrors((e) => ({ ...e, skills: err }));
    if (err) return;
    const next = [...skills, t];
    setSkills(next);
    setSkillDraft("");
    await flyTo(fromEl, slotEl("skills"), t);
    setCard((c) => ({ ...c, skills: next }));
    flash("skills");
    setAnnounce(`${t} added to your skills.`);
  };
  const removeSkill = (t: string): void => {
    const next = skills.filter((s) => s !== t);
    setSkills(next);
    setCard((c) => ({ ...c, skills: next }));
    setErrors((e) => ({ ...e, skills: "" }));
  };

  // ── resume ──────────────────────────────────────────────────────────────
  const takeFile = async (f: File | null): Promise<void> => {
    setExtractNote(null);
    if (!f) return;
    if (f.size === 0) return setErrors((e) => ({ ...e, resume: "That file looks empty — please pick another." }));
    if (f.size > MAX_BYTES) return setErrors((e) => ({ ...e, resume: "That file is larger than 10 MB. Please upload a smaller PDF or Word document." }));
    setErrors((e) => ({ ...e, resume: "" }));
    setFile(f);
    setCard((c) => ({ ...c, resume: f.name }));
    flash("resume");

    // Read the CV and prefill what's still empty, so the candidate verifies instead of retyping.
    setIsExtracting(true);
    try {
      const res = await api.post<{ ok: boolean; extracted: ExtractedFacts }>(`/candidate-profile/${token}/extract`, {
        resume: { filename: f.name, mime: f.type || "application/octet-stream", contentBase64: await fileToBase64(f) },
      });
      const ext = res?.ok ? res.extracted : null;
      if (ext) {
        const fills: Partial<Record<TextKey, string>> = {};
        const empty = (k: TextKey): boolean => !(draft[k] ?? "").trim();
        if (ext.candidateName && empty("name")) fills.name = ext.candidateName;
        if (ext.email && empty("email")) fills.email = ext.email;
        if (ext.phone && empty("phone")) fills.phone = ext.phone;
        if (ext.linkedinUrl && empty("linkedin")) fills.linkedin = ext.linkedinUrl;
        if (ext.location && empty("location")) fills.location = ext.location;
        if (ext.experienceYears != null && empty("experience")) fills.experience = String(ext.experienceYears);
        if (ext.noticePeriodDays != null && empty("notice")) {
          const n = ext.noticePeriodDays;
          const exact = NOTICE_OPTIONS.find((o) => o.value === String(n));
          if (exact) fills.notice = exact.value;
          else if (n > 90) { fills.notice = "more"; fills.noticeDays = String(n); }
        }
        const valid = Object.entries(fills).filter(([k, v]) => !validate(k as TextKey, v)) as [TextKey, string][];
        if (valid.length) {
          setDraft((d) => ({ ...d, ...Object.fromEntries(valid) }));
          setCard((c) => ({ ...c, ...Object.fromEntries(valid) }));
          for (const [k] of valid) flash(slotFor(k));
          setExtractNote(`We filled in ${valid.length} detail${valid.length === 1 ? "" : "s"} from your CV — please check them.`);
          setAnnounce(`${valid.length} details filled in from your CV.`);
        }
      }
    } catch {
      // Reading the CV is a convenience; the upload itself still goes through on submit.
    } finally {
      setIsExtracting(false);
    }
  };
  const onPick = (e: ChangeEvent<HTMLInputElement>): void => {
    void takeFile(e.target.files?.[0] ?? null);
    e.target.value = "";
  };
  const onDrop = (e: DragEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setDragOver(false);
    void takeFile(e.dataTransfer.files?.[0] ?? null);
  };

  // ── submit ──────────────────────────────────────────────────────────────
  const submit = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      const t = (k: TextKey): string => (draft[k] ?? "").trim();
      if (t("name")) body.candidateName = t("name");
      if (t("email")) body.email = t("email");
      if (t("phone")) body.phone = t("phone");
      if (t("linkedin")) body.linkedinUrl = t("linkedin");
      if (t("location")) body.location = t("location");
      if (t("experience")) body.experienceYears = Number(t("experience"));
      if (t("grad")) body.graduationYear = Number(t("grad"));
      if (t("about")) body.about = t("about");
      if (skills.length) body.skills = skills;
      if (t("notice")) body.noticePeriodDays = t("notice") === "more" ? Number(t("noticeDays")) : Number(t("notice"));
      if (t("notice") === "0" && t("lwd")) body.lastWorkingDate = t("lwd");
      if (asks("currentCtc") && t("currentCtc")) body.currentCtc = t("currentCtc");
      if (asks("expectedCtc") && t("expectedCtc")) body.expectedCtc = t("expectedCtc");
      if (asks("visaNeeded") && t("visa")) body.visaNeeded = t("visa") === "yes";
      if (file) {
        body.resume = { filename: file.name, mime: file.type || "application/octet-stream", contentBase64: await fileToBase64(file) };
      }
      return api.post<SubmitResult>(`/candidate-profile/${token}/submit`, body);
    },
    onSuccess: (res) => setResult(res),
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    const found: [string, string][] = [];
    const nextErrors: typeof errors = {};
    if (reqInfo.needsResume && !file) {
      nextErrors.resume = "Attach your resume — it's the one thing this form needs.";
      found.push(["cp-resume", nextErrors.resume]);
    }
    const keys: TextKey[] = ["name", "email", "phone", "linkedin", "location", "grad", "experience", "notice"];
    if (draft.notice === "more") keys.push("noticeDays");
    if (draft.notice === "0") keys.push("lwd");
    if (asks("currentCtc")) keys.push("currentCtc");
    if (asks("expectedCtc")) keys.push("expectedCtc");
    for (const k of keys) {
      const err = validate(k, draft[k] ?? "");
      nextErrors[k] = err;
      if (err) found.push([`cp-${k}`, `${LABEL[k]}: ${err}`]);
    }
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    const hasAnything =
      Boolean(file) || keys.some((k) => (draft[k] ?? "").trim()) || skills.length > 0 || Boolean((draft.about ?? "").trim());
    if (!found.length && !hasAnything) found.push(["cp-name", "Fill in your details or attach your resume before submitting."]);
    setSummary(found);
    if (found.length) {
      requestAnimationFrame(() => {
        summaryRef.current?.focus({ preventScroll: true });
        summaryRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
      });
      return;
    }
    submit.mutate();
  };

  // ── progress ────────────────────────────────────────────────────────────
  const tracked = useMemo(() => {
    const t: (keyof CardState)[] = ["name", "email", "phone", "location", "skills", "grad", "experience", "notice", "about"];
    if (reqInfo.needsResume) t.unshift("resume");
    if (asks("currentCtc")) t.push("currentCtc");
    if (asks("expectedCtc")) t.push("expectedCtc");
    return reqInfo.needsProfile ? t : (["resume"] as (keyof CardState)[]);
  }, [reqInfo, asks]);
  const filled = tracked.filter((k) => {
    const v = card[k];
    return Array.isArray(v) ? v.length > 0 : Boolean(v);
  }).length;
  const pct = Math.round((filled / tracked.length) * 100);

  const groupDone = {
    resume: Boolean(card.resume),
    personal: Boolean(card.name && (card.email || card.phone)),
    background: Boolean(card.skills.length && (card.grad || card.experience)),
    availability: Boolean(card.notice && (!asks("expectedCtc") || card.expectedCtc)),
  };

  if (result) return <Done reqInfo={reqInfo} result={result} card={card} asks={asks} />;
  if (reqInfo.completed) return <Done reqInfo={reqInfo} result={null} card={card} asks={asks} />;

  const expires = new Date(reqInfo.expiresAt);
  const cardProps = { card, asks, reqInfo, pct, flashing, cardRef } as const;
  const f = (k: TextKey): FieldBind => ({
    id: `cp-${k}`,
    value: draft[k] ?? "",
    error: errors[k] ?? "",
    shake: shaking[k] ?? 0,
    valid: Boolean(card[k]) && !errors[k],
    onChange: (v) => set(k, v),
    onFinish: (el, fresh) => void finish(k, el, fresh),
  });
  let group = 0;

  return (
    <Frame wide>
      <header className="cp-top">
        <span className="cp-pill">Secure link</span>
        <h1>Complete your application</h1>
        <p>
          <b>{reqInfo.companyName}</b> needs a little more from you for the <b>{reqInfo.jobTitle}</b> role. It takes a
          few minutes, and you don't need an account. Your card preview shows what the hiring team will see.
        </p>
      </header>

      <div className="cp-layout">
        <form className="cp-form" onSubmit={onSubmit} noValidate>
          <p className="cp-reqnote">
            {reqInfo.needsResume ? (
              <>Fields marked <Req /> are required — everything else is optional, so fill in what you can.</>
            ) : (
              <>Everything here is optional — fill in what you can.</>
            )}
          </p>
          {reqInfo.note && (
            <div className="cp-banner info">
              <IconInfo />
              <div>
                <b>A note from {reqInfo.companyName}</b>
                <div>{reqInfo.note}</div>
              </div>
            </div>
          )}

          {summary.length > 0 && (
            <div className="cp-summary" ref={summaryRef} tabIndex={-1} role="alert" aria-labelledby="cp-summary-title">
              <h2 id="cp-summary-title">
                <IconAlert /> {summary.length === 1 ? "Fix 1 thing to send your application" : `Fix ${summary.length} things to send your application`}
              </h2>
              <ul>
                {summary.map(([id, msg]) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(id);
                        el?.focus();
                        el?.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
                      }}
                    >
                      {msg}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {submit.isError && (
            <div className="cp-banner crit" role="alert">
              <IconAlert />
              <div>
                {submit.error instanceof ApiError && typeof submit.error.body === "object" && submit.error.body !== null
                  ? ((submit.error.body as { message?: string }).message ?? "Something went wrong. Please try again.")
                  : "Something went wrong. Please try again."}
              </div>
            </div>
          )}

          {reqInfo.needsResume && (
            <Group n={++group} title="Your resume" sub="PDF or Word works best · up to 10 MB" done={groupDone.resume}>
              <div className={`cp-field cp-full${errors.resume ? " is-invalid" : ""}`}>
                <span className="cp-label" id="cp-resume-label">
                  Resume <Req />
                </span>
                <button
                  id="cp-resume"
                  type="button"
                  className={`cp-drop${dragOver ? " is-over" : ""}${file ? " has-file" : ""}`}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  aria-labelledby="cp-resume-label"
                  aria-describedby="cp-resume-err"
                  disabled={isExtracting}
                >
                  <span className="cp-drop-icon">{isExtracting ? <span className="cp-spin" /> : file ? <IconFile /> : <IconUpload />}</span>
                  <span className="cp-drop-text">
                    <strong>{isExtracting ? "Reading your CV…" : file ? file.name : "Choose your resume, or drop it here"}</strong>
                    <span>
                      {isExtracting
                        ? "Filling in what we can find — you'll check everything"
                        : file
                          ? `${Math.max(1, Math.round(file.size / 1024))} KB · click to choose a different file`
                          : "PDF, DOC, DOCX, RTF or TXT"}
                    </span>
                  </span>
                </button>
                <input ref={fileRef} type="file" accept={ACCEPT} onChange={onPick} hidden />
                {extractNote && (
                  <p className="cp-note good">
                    <IconCheck /> {extractNote}
                  </p>
                )}
                <Err id="cp-resume-err" msg={errors.resume ?? ""} />
              </div>
            </Group>
          )}

          {reqInfo.needsProfile && (
            <>
              <Group n={++group} title="Personal details" sub="How the hiring team will address and reach you" done={groupDone.personal}>
                <TextField label="Full name" icon={<IconUser />} autoComplete="name" placeholder="e.g. Lakshmi Pillai" {...f("name")} />
                <TextField label="Email" icon={<IconMail />} type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" {...f("email")} />
                <TextField label="Phone" icon={<IconPhone />} type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 9876543210" {...f("phone")} />
                <TextField label="City" icon={<IconPin />} autoComplete="address-level2" placeholder="e.g. Chennai" {...f("location")} />
                <TextField label="LinkedIn" icon={<IconLink />} full type="url" inputMode="url" autoComplete="url" placeholder="linkedin.com/in/your-name" {...f("linkedin")} />
              </Group>

              <Group n={++group} title="Background" sub="Your skills and where you are in your career" done={groupDone.background}>
                <div className={`cp-field cp-full${errors.skills ? " is-invalid" : ""}`}>
                  <label className="cp-label" htmlFor="cp-skills">
                    Skills <span className="cp-opt">Up to {MAX_SKILLS}</span>
                  </label>
                  <div className="cp-tags" onClick={(e) => e.target === e.currentTarget && document.getElementById("cp-skills")?.focus()}>
                    <span className="cp-lead" aria-hidden="true"><IconTag /></span>
                    {skills.map((s) => (
                      <span className="cp-tag" key={s}>
                        {s}
                        <button type="button" onClick={() => removeSkill(s)} aria-label={`Remove ${s}`}>
                          <IconX />
                        </button>
                      </span>
                    ))}
                    <input
                      id="cp-skills"
                      value={skillDraft}
                      placeholder={skills.length ? "Add another" : "Type a skill, press Enter"}
                      autoComplete="off"
                      aria-describedby="cp-skills-hint cp-skills-err"
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.endsWith(",")) void addSkill(v, e.currentTarget);
                        else setSkillDraft(v);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void addSkill(skillDraft, e.currentTarget);
                        } else if (e.key === "Backspace" && !skillDraft && skills.length) {
                          removeSkill(skills[skills.length - 1]!);
                        }
                      }}
                      onBlur={(e) => skillDraft.trim() && void addSkill(skillDraft, e.currentTarget)}
                    />
                  </div>
                  <p className="cp-hint" id="cp-skills-hint">Press Enter or comma to add. Backspace removes the last one.</p>
                  <Err id="cp-skills-err" msg={errors.skills ?? ""} />
                </div>
                <TextField label="Graduation year" icon={<IconCap />} inputMode="numeric" maxLength={4} placeholder={`e.g. ${YEAR_NOW - 1}`} {...f("grad")} />
                <TextField label="Years of experience" icon={<IconBriefcase />} inputMode="numeric" maxLength={2} placeholder="e.g. 3" hint="0 is fine if you're a fresher" {...f("experience")} />
                <TextAreaField label="About you" full placeholder="A few lines on what you do, what you're good at, and what you're looking for." max={600} {...f("about")} />
              </Group>

              <Group
                n={++group}
                title="Availability & pay"
                sub={asks("currentCtc") || asks("expectedCtc") ? "When you can join, and what you earn and expect" : "When you can join"}
                done={groupDone.availability}
              >
                <SelectField label="Notice period" icon={<IconClock />} options={NOTICE_OPTIONS} placeholder="Select notice period" {...f("notice")} />
                {draft.notice === "0" && (
                  <TextField
                    label="Last working date"
                    icon={<IconCalendar />}
                    type="date"
                    hint="Your last day at your current job, if you have one"
                    reveal
                    {...f("lwd")}
                  />
                )}
                {draft.notice === "more" && (
                  <TextField
                    label="Notice period (days)"
                    icon={<IconClock />}
                    inputMode="numeric"
                    maxLength={3}
                    placeholder="e.g. 120"
                    required
                    reveal
                    {...f("noticeDays")}
                  />
                )}
                {asks("currentCtc") && (
                  <TextField label="Current CTC" icon={<IconRupee />} placeholder="e.g. ₹12 LPA" hint="Your current annual pay" {...f("currentCtc")} />
                )}
                {asks("expectedCtc") && (
                  <TextField label="Expected CTC" icon={<IconRupee />} placeholder="e.g. ₹16 LPA" hint="What you're looking for" {...f("expectedCtc")} />
                )}
                {asks("visaNeeded") && (
                  <SelectField
                    label="Will you need visa sponsorship?"
                    full
                    options={[
                      { value: "no", label: "No" },
                      { value: "yes", label: "Yes" },
                    ]}
                    placeholder="Prefer not to say"
                    {...f("visa")}
                  />
                )}
              </Group>
            </>
          )}

          <div className="cp-foot">
            <p>
              <Req /> Required. This link is personal to you and expires on{" "}
              {expires.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}.
            </p>
            <button type="submit" className="cp-btn" disabled={submit.isPending || isExtracting}>
              {submit.isPending ? (
                <>
                  <span className="cp-spin" aria-hidden="true" /> Sending…
                </>
              ) : (
                <>
                  <IconSend /> Send my application
                </>
              )}
            </button>
          </div>
        </form>

        <aside className="cp-aside" aria-label="Your card preview">
          <div className="cp-aside-label">
            <span>Your card</span>
            <span className="cp-live">Live preview</span>
          </div>
          <CandidateCard {...cardProps} />
        </aside>
      </div>

      <button
        type="button"
        className="cp-peek"
        onClick={() => cardRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" })}
        aria-label="Jump to your card preview"
      >
        <Avatar name={card.name ?? ""} small />
        <span>
          <b>{card.name || "Your card"}</b>
          <small>{pct}% complete</small>
        </span>
        <span className="cp-peek-go">Preview ↓</span>
      </button>
      <div className="cp-sr" aria-live="polite">{announce}</div>
    </Frame>
  );
}

function slotFor(k: TextKey): string {
  if (k === "email" || k === "phone" || k === "linkedin") return k;
  if (k === "notice" || k === "noticeDays") return "notice";
  return k;
}

// ── the card ──────────────────────────────────────────────────────────────────

function CandidateCard({
  card,
  asks,
  reqInfo,
  pct,
  flashing,
  cardRef,
}: {
  card: CardState;
  asks: (f: string) => boolean;
  reqInfo: ProfileRequest;
  pct?: number;
  flashing?: Record<string, number>;
  cardRef?: RefObject<HTMLElement | null>;
}): ReactNode {
  const fl = (slot: string): string => (flashing?.[slot] ? ` cp-flash-${flashing[slot]! % 2}` : "");
  const C = 138.2;
  const notice = noticeText(card);
  const showPay = asks("currentCtc") || asks("expectedCtc");
  return (
    <article className="cp-card" ref={cardRef as RefObject<HTMLElement>}>
      <div className="cp-card-job">
        Applying for <b>{reqInfo.jobTitle}</b> · {reqInfo.companyName}
      </div>
      {pct !== undefined && (
        <div className="cp-ring" title="Profile completeness" aria-label={`Profile ${pct}% complete`}>
          <svg viewBox="0 0 52 52" aria-hidden="true">
            <circle cx="26" cy="26" r="22" className="cp-ring-track" />
            <circle cx="26" cy="26" r="22" className="cp-ring-bar" strokeDasharray={C} strokeDashoffset={C - (C * pct) / 100} />
          </svg>
          <b>{pct}%</b>
        </div>
      )}

      <div className="cp-avatar-wrap">
        <Avatar name={card.name ?? ""} />
      </div>
      <h2 className={`cp-card-name cp-slot${fl("name")}`} data-slot="name">
        {card.name || <span className="cp-ph">Your name</span>}
      </h2>
      <div className="cp-card-contact">
        <span className={`cp-slot${fl("email")}`} data-slot="email">
          {card.email || <span className="cp-ph">email</span>}
        </span>
        {card.email && card.phone && <span className="cp-sep" aria-hidden="true">•</span>}
        <span className={`cp-slot${fl("phone")}`} data-slot="phone">{card.phone ? formatPhone(card.phone) : ""}</span>
      </div>
      <div className={`cp-card-sub cp-slot${fl("linkedin")}`} data-slot="linkedin">
        {card.linkedin ? card.linkedin.replace(/^https?:\/\/(www\.)?/i, "") : ""}
      </div>
      {reqInfo.needsResume && (
        <div className={`cp-card-cv cp-slot${fl("resume")}`} data-slot="resume">
          {card.resume ? (
            <>
              <IconFile /> {card.resume}
            </>
          ) : (
            <span className="cp-ph">No resume attached yet</span>
          )}
        </div>
      )}

      <section className="cp-card-sec">
        <h3>About Me</h3>
        <p className={`cp-card-about cp-slot${fl("about")}`} data-slot="about">
          {card.about || <span className="cp-ph">Your introduction appears here.</span>}
        </p>
      </section>

      <section className="cp-card-sec">
        <h3>Skills &amp; Background</h3>
        <div className={`cp-chips cp-slot${fl("skills")}`} data-slot="skills">
          {card.skills.length ? card.skills.map((s) => <span className="cp-chip" key={s}>{s}</span>) : <span className="cp-ph">Add a few skills</span>}
        </div>
        <dl className="cp-kv">
          <div>
            <dt>Graduation</dt>
            <dd className={`cp-slot${fl("grad")}`} data-slot="grad">{card.grad || <span className="cp-ph">—</span>}</dd>
          </div>
          <div>
            <dt>Experience</dt>
            <dd className={`cp-slot${fl("experience")}`} data-slot="experience">
              {card.experience !== undefined ? (card.experience === "0" ? "Fresher" : `${card.experience} yrs`) : <span className="cp-ph">—</span>}
            </dd>
          </div>
        </dl>
      </section>

      <section className="cp-card-sec">
        <h3>{showPay ? "Availability & Pay" : "Availability"}</h3>
        <dl className="cp-kv">
          <div>
            <dt>Notice period</dt>
            <dd className={`cp-slot${fl("notice")}`} data-slot="notice">{notice || <span className="cp-ph">—</span>}</dd>
          </div>
          {card.notice === "0" && (
            <div>
              <dt>Last working date</dt>
              <dd className={`cp-slot${fl("lwd")}`} data-slot="lwd">{card.lwd ? dateText(card.lwd) : <span className="cp-ph">—</span>}</dd>
            </div>
          )}
          {asks("currentCtc") && (
            <div>
              <dt>Current CTC</dt>
              <dd className={`cp-slot${fl("currentCtc")}`} data-slot="currentCtc">{card.currentCtc || <span className="cp-ph">—</span>}</dd>
            </div>
          )}
          {asks("expectedCtc") && (
            <div>
              <dt>Expected CTC</dt>
              <dd className={`cp-slot${fl("expectedCtc")}`} data-slot="expectedCtc">{card.expectedCtc || <span className="cp-ph">—</span>}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className="cp-card-sec">
        <h3>Location</h3>
        <p className="cp-card-loc">
          <IconPin />
          <span className={`cp-slot${fl("location")}`} data-slot="location">{card.location || <span className="cp-ph">Your city</span>}</span>
        </p>
      </section>
    </article>
  );
}

function Avatar({ name, small = false }: { name: string; small?: boolean }): ReactNode {
  const h = name ? hueOf(name) : 0;
  return (
    <span
      key={name ? initials(name) : "empty"}
      className={`cp-avatar${small ? " is-small" : ""}${name ? "" : " is-empty"}`}
      style={name ? ({ "--av1": `hsl(${h} 70% 30%)`, "--av2": `hsl(${h + 20} 55% 52%)` } as CSSProperties) : undefined}
      aria-hidden="true"
    >
      {name ? initials(name) : <IconUser />}
    </span>
  );
}

// ── done ──────────────────────────────────────────────────────────────────────

function Done({
  reqInfo,
  result,
  card,
  asks,
}: {
  reqInfo: ProfileRequest;
  result: SubmitResult | null;
  card: CardState;
  asks: (f: string) => boolean;
}): ReactNode {
  const hasCard = Boolean(card.name || card.email || card.skills.length);
  return (
    <Frame wide={hasCard}>
      <div className={hasCard ? "cp-layout cp-done-layout" : ""}>
        <div className="cp-panel cp-done">
          <span className="cp-done-mark" aria-hidden="true"><IconCheck /></span>
          <h1>Thanks — your application is on its way</h1>
          <p>
            Your details have been sent to the hiring team at <b>{result?.companyName ?? reqInfo.companyName}</b> for{" "}
            <b>{result?.jobTitle ?? reqInfo.jobTitle}</b>. They'll be in touch if they'd like to take things further.
          </p>
          {/* Honest about the RESUME specifically. "We got everything" when the file could not be
              read is the one message here that would cost the candidate the application. */}
          {result?.resumeReceived && !result.resumeReadable && (
            <div className="cp-banner warn">
              <IconInfo />
              <div>
                We saved your file, but couldn't read any text from it — the recruiter will open it themselves. If it was a
                scan or an image, sending a PDF or Word version helps them find you.
              </div>
            </div>
          )}
          {result && !result.resumeReceived && reqInfo.needsResume && (
            <div className="cp-banner warn">
              <IconInfo />
              <div>We've saved your details, but no resume was attached. Reply to the email you received if you'd like to send one.</div>
            </div>
          )}
        </div>
        {hasCard && (
          <aside className="cp-aside" aria-label="What the hiring team sees">
            <div className="cp-aside-label">
              <span>What the hiring team sees</span>
            </div>
            <CandidateCard card={card} asks={asks} reqInfo={reqInfo} />
          </aside>
        )}
      </div>
    </Frame>
  );
}

// ── form building blocks ──────────────────────────────────────────────────────

interface FieldBind {
  id: string;
  value: string;
  error: string;
  shake: number;
  valid: boolean;
  onChange: (v: string) => void;
  onFinish: (el: Element | null, fresh?: string) => void;
}

function Group({ n, title, sub, done, children }: { n: number; title: string; sub: string; done: boolean; children: ReactNode }): ReactNode {
  return (
    <fieldset className={`cp-group${done ? " is-done" : ""}`}>
      <legend className="cp-legend">
        <span className="cp-num" aria-hidden="true">{n}</span>
        <span>
          <span className="cp-group-title">
            {title}
            {done && (
              <span className="cp-done-tag">
                <IconCheck /> Filled in
              </span>
            )}
          </span>
          <span className="cp-group-sub">{sub}</span>
        </span>
      </legend>
      <div className="cp-fields">{children}</div>
    </fieldset>
  );
}

const Req = (): ReactNode => (
  <>
    <span className="cp-req" aria-hidden="true">*</span>
    <span className="cp-sr">required</span>
  </>
);

function Err({ id, msg }: { id: string; msg: string }): ReactNode {
  return (
    <div className={`cp-err${msg ? " is-shown" : ""}`}>
      <p id={id} role={msg ? "alert" : undefined}>
        {msg && (
          <>
            <IconAlert /> <span>{msg}</span>
          </>
        )}
      </p>
    </div>
  );
}

function TextField({
  label,
  icon,
  full,
  hint,
  reveal,
  required,
  type = "text",
  inputMode,
  autoComplete,
  placeholder,
  maxLength,
  id,
  value,
  error,
  shake,
  valid,
  onChange,
  onFinish,
}: FieldBind & {
  label: string;
  icon?: ReactNode;
  full?: boolean;
  hint?: string;
  reveal?: boolean;
  required?: boolean;
  type?: string;
  inputMode?: "text" | "email" | "tel" | "numeric" | "url";
  autoComplete?: string;
  placeholder?: string;
  maxLength?: number;
}): ReactNode {
  const hintId = hint ? `${id}-hint` : "";
  return (
    <div className={`cp-field${full ? " cp-full" : ""}${error ? " is-invalid" : valid ? " is-valid" : ""}${reveal ? " cp-reveal" : ""}`}>
      <label className="cp-label" htmlFor={id}>
        {label} {required ? <Req /> : null}
      </label>
      <div className="cp-control" key={`shake-${shake}`} data-shake={shake > 0 || undefined}>
        {icon && <span className="cp-lead" aria-hidden="true">{icon}</span>}
        <input
          id={id}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          placeholder={placeholder}
          maxLength={maxLength}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={[hintId, `${id}-err`].filter(Boolean).join(" ")}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onFinish(e.currentTarget)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            // Enter moves on, like Tab — the field is finished.
            const all = Array.from(document.querySelectorAll<HTMLElement>(".cp-form input:not([type=file]), .cp-form select, .cp-form textarea"));
            const next = all[all.indexOf(e.currentTarget) + 1];
            if (next) next.focus();
            else e.currentTarget.blur();
          }}
        />
        <span className="cp-state ok" aria-hidden="true"><IconCheck /></span>
        <span className="cp-state bad" aria-hidden="true"><IconAlert /></span>
      </div>
      {hint && <p className="cp-hint" id={hintId}>{hint}</p>}
      <Err id={`${id}-err`} msg={error} />
    </div>
  );
}

function TextAreaField({
  label,
  full,
  placeholder,
  max,
  id,
  value,
  error,
  onChange,
  onFinish,
}: FieldBind & { label: string; full?: boolean; placeholder?: string; max: number }): ReactNode {
  return (
    <div className={`cp-field${full ? " cp-full" : ""}${error ? " is-invalid" : ""}`}>
      <label className="cp-label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        rows={4}
        maxLength={max}
        placeholder={placeholder}
        value={value}
        aria-describedby={`${id}-hint ${id}-err`}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onFinish(e.currentTarget)}
      />
      <p className="cp-hint cp-hint-row" id={`${id}-hint`}>
        <span>Two or three sentences work best.</span>
        <span className={`cp-count${value.length > max - 40 ? " near" : ""}`}>
          {value.length} / {max}
        </span>
      </p>
      <Err id={`${id}-err`} msg={error} />
    </div>
  );
}

function SelectField({
  label,
  icon,
  full,
  options,
  placeholder,
  id,
  value,
  error,
  shake,
  valid,
  onChange,
  onFinish,
}: FieldBind & { label: string; icon?: ReactNode; full?: boolean; options: { value: string; label: string }[]; placeholder: string }): ReactNode {
  return (
    <div className={`cp-field${full ? " cp-full" : ""}${error ? " is-invalid" : valid ? " is-valid" : ""}`}>
      <label className="cp-label" htmlFor={id}>
        {label}
      </label>
      <div className="cp-control" key={`shake-${shake}`}>
        {icon && <span className="cp-lead" aria-hidden="true">{icon}</span>}
        <select
          id={id}
          className={icon ? "" : "cp-no-lead"}
          value={value}
          aria-describedby={`${id}-err`}
          onChange={(e) => {
            onChange(e.target.value);
            // A choice is complete the moment it's made.
            onFinish(e.currentTarget, e.target.value);
          }}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <Err id={`${id}-err`} msg={error} />
    </div>
  );
}

/** The page frame — brand bar, then the content. */
function Frame({ children, wide = false }: { children: ReactNode; wide?: boolean }): ReactNode {
  return (
    <div className="cp-page">
      <div className={`cp-wrap${wide ? " is-wide" : ""}`}>
        <div className="cp-brand">
          <span className="cp-logo">TH</span>
          <span>TalentHop</span>
        </div>
        {children}
        <p className="cp-copy">© {new Date().getFullYear()} TalentHop · Your details go only to the hiring team you applied to.</p>
      </div>
    </div>
  );
}

// ── icons (inline, stroke style, decorative) ─────────────────────────────────

const Svg = ({ children }: { children: ReactNode }): ReactNode => (
  <svg className="cp-icon" viewBox="0 0 24 24" aria-hidden="true">
    {children}
  </svg>
);
const IconUser = (): ReactNode => <Svg><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Svg>;
const IconMail = (): ReactNode => <Svg><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" /></Svg>;
const IconPhone = (): ReactNode => <Svg><path d="M6.6 3h2.6l1.6 4.2-2 1.3a11 11 0 0 0 6.7 6.7l1.3-2 4.2 1.6v2.6A1.6 1.6 0 0 1 19.4 19 16 16 0 0 1 5 4.6 1.6 1.6 0 0 1 6.6 3Z" /></Svg>;
const IconPin = (): ReactNode => <Svg><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" /><circle cx="12" cy="10" r="2.4" /></Svg>;
const IconLink = (): ReactNode => <Svg><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></Svg>;
const IconUpload = (): ReactNode => <Svg><path d="M12 15V4m0 0L7.5 8.5M12 4l4.5 4.5" /><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></Svg>;
const IconFile = (): ReactNode => <Svg><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></Svg>;
const IconCheck = (): ReactNode => <Svg><path d="m5 12.5 4.5 4.5L19 7.5" /></Svg>;
const IconAlert = (): ReactNode => <Svg><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.3v.2" /></Svg>;
const IconInfo = (): ReactNode => <Svg><circle cx="12" cy="12" r="9" /><path d="M12 11v5.5M12 7.7v.2" /></Svg>;
const IconCap = (): ReactNode => <Svg><path d="m2.5 9 9.5-4.5L21.5 9 12 13.5 2.5 9Z" /><path d="M6.5 11v4.5c0 1.4 2.5 3 5.5 3s5.5-1.6 5.5-3V11" /></Svg>;
const IconBriefcase = (): ReactNode => <Svg><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" /></Svg>;
const IconTag = (): ReactNode => <Svg><path d="M3.5 12V4.5a1 1 0 0 1 1-1H12l8.5 8.5-8.5 8.5L3.5 12Z" /><circle cx="8" cy="8" r="1.4" /></Svg>;
const IconX = (): ReactNode => <Svg><path d="M6 6l12 12M18 6 6 18" /></Svg>;
const IconClock = (): ReactNode => <Svg><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Svg>;
const IconCalendar = (): ReactNode => <Svg><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></Svg>;
const IconRupee = (): ReactNode => <Svg><path d="M7 4h11M7 9h11M7 4h3a5 5 0 0 1 0 10H7l8 7" /></Svg>;
const IconSend = (): ReactNode => <Svg><path d="M21 3 10 14M21 3l-7 18-4-7-7-4 18-7Z" /></Svg>;
